// Shared helper utilities for the invoice app
import QRCode from 'qrcode';

export const isValidIco = (ico: string) => /^\d{8}$/.test(ico || "");

export const pad2 = (n: number): string => String(n).padStart(2, "0");

export const calculateDueDate = (issueDate: string, days: number): string => {
    if (!issueDate) return "";
    const date = new Date(issueDate);
    if (Number.isNaN(date.getTime())) return issueDate;
    date.setDate(date.getDate() + days);
    return date.toISOString().split("T")[0];
};

export const getDaysDifference = (date1: string, date2: string): number => {
    if (!date1 || !date2) return 0;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
    const diffTime = d2.getTime() - d1.getTime();
    return Math.round(diffTime / (1000 * 60 * 60 * 24));
};

export const formatAmount = (amount: number, currency?: string): string => {
    if (typeof amount !== 'number') return String(amount || "");
    const formatted = amount.toLocaleString("cs-CZ", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    
    if (currency === 'CZK') {
        return `${formatted} Kč`;
    }
    
    return currency ? `${formatted} ${currency}` : formatted;
};

export function formatDate(iso: string): string {
    if (!iso) return "";
    try {
        const [Y, M, D] = iso.split("-").map((x) => parseInt(x, 10));

        // Check if any part is NaN
        if (isNaN(Y) || isNaN(M) || isNaN(D)) {
            return "";
        }

        const d = new Date(Date.UTC(Y, (M || 1) - 1, D || 1));

        // Check if the date is valid
        if (isNaN(d.getTime())) {
            return "";
        }

        const dd = String(d.getUTCDate()).padStart(2, "0");
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        const yyyy = d.getUTCFullYear();
        return `${dd}.${mm}.${yyyy}`;
    } catch (error) {
        return "";
    }
}


// Render invoice number by template.
// Supports two token styles simultaneously:
// 1) With braces: {YYYY},{YY},{MM},{DD},{customer},{project},{seq},{seq:n}
// 2) Plain tokens: YYYY,YY,MM,DD,NN (NN means 2-digit sequence)
export function renderInvoiceFromTemplate(
    template: string,
    opts: { date?: Date | string; customer?: string; project?: string; seq?: number }
): string {
    try {
        if (!template || typeof template !== 'string') return '';
        const seq = Number.isFinite(opts.seq as number) ? (opts.seq as number) : 1;
        const d = (() => {
            if (!opts.date) return new Date();
            if (opts.date instanceof Date) return opts.date;
            const parsed = new Date(opts.date);
            return isNaN(parsed.getTime()) ? new Date() : parsed;
        })();

        const yyyy = d.getFullYear();
        const yy = String(yyyy).slice(-2);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const customer = (opts.customer || '').trim().toUpperCase();
        const project = (opts.project || '').trim().toUpperCase();

        // First, handle {seq:n}
        let out = template.replace(/\{seq:(\d+)\}/g, (_m, p1) => String(seq).padStart(parseInt(p1, 10), '0'));

        // Then handle {seq}
        out = out.replace(/\{seq\}/g, String(seq));

        // Replace brace tokens
        out = out.replace(/\{YYYY\}/g, String(yyyy))
            .replace(/\{YY\}/g, yy)
            .replace(/\{MM\}/g, mm)
            .replace(/\{DD\}/g, dd)
            .replace(/\{customer\}/g, customer)
            .replace(/\{project\}/g, project);

        // Replace plain tokens (no braces). NN uses 2-digit sequence.
        out = out.replace(/YYYY/g, String(yyyy))
            .replace(/YY/g, yy)
            .replace(/MM/g, mm)
            .replace(/DD/g, dd)
            .replace(/NN/g, String(seq).padStart(2, '0'));

        return out;
    } catch {
        return '';
    }
}

/**
 * Generate SPAYD format string for Czech banks QR code
 * Format: SPD*1.0*ACC:CZ6508000000192000145399*AM:450.00*CC:CZK*MSG:Payment for invoice 123*VS:2025001*...
 */
/**
 * Convert Czech account format (XXXXXX-YYYYYYYYYYYY/CCCC) to IBAN
 * Format: accountNumber = "19-2000145399", bankCode = "0800"
 * Returns: "CZ6508000000192000145399"
 * 
 * Note: This is a helper function for future use. For now, QR codes are generated only from IBAN.
 * If accountNumber and bankCode are provided, they will need IBAN conversion logic.
 */
export function convertCzechAccountToIBAN(accountNumber: string, bankCode: string): string | null {
    try {
        // Remove dashes and slashes
        const cleanAccount = accountNumber.replace(/[-\s]/g, '');
        // Pad with zeros to 16 digits
        const paddedAccount = cleanAccount.padStart(16, '0');
        // Combine: CZ + check + bankCode + account
        const prefix = `CZ00${bankCode}`;
        const combined = `${prefix}${paddedAccount}`;

        // Calculate checksum (mod 97)
        let sum = 0;
        for (let i = 0; i < combined.length; i++) {
            const char = combined[i];
            const value = /[0-9]/.test(char) ? parseInt(char, 10) : (char.charCodeAt(0) - 55);
            sum = (sum * 10 + value) % 97;
        }
        const checksum = 98 - sum;

        return `CZ${String(checksum).padStart(2, '0')}${bankCode}${paddedAccount}`;
    } catch {
        return null;
    }
}

export function generateSPAYDString(data: {
    account: string;        // IBAN: CZ6508000000192000145399
    bic?: string;           // Optional BIC/SWIFT code
    amount?: number;        // 450.50
    currency?: string;      // CZK
    variableSymbol?: string; // X-VS: 2025001 (Czech banks)
    constantSymbol?: string; // X-KS: 0008 (Czech banks)
    specificSymbol?: string; // X-SS: 1234 (Czech banks)
    message?: string;       // MSG: Payment for invoice
    dueDate?: string;       // DT: 20241101 (YYYYMMDD)
}): string {
    const parts: string[] = ['SPD*1.0'];

    // Build ACC field: IBAN or IBAN+BIC
    if (data.account) {
        const accValue = data.bic ? `${data.account}+${data.bic}` : data.account;
        parts.push(`ACC:${accValue}`);
    }

    if (data.amount !== undefined) parts.push(`AM:${data.amount.toFixed(2)}`);
    if (data.currency) parts.push(`CC:${data.currency}`);
    // Use Czech-specific symbols (X-VS, X-KS, X-SS)
    if (data.variableSymbol) parts.push(`X-VS:${data.variableSymbol}`);
    if (data.constantSymbol) parts.push(`X-KS:${data.constantSymbol}`);
    if (data.specificSymbol) parts.push(`X-SS:${data.specificSymbol}`);
    if (data.message) parts.push(`MSG:${data.message.substring(0, 60).toUpperCase()}`); // Max 60 chars, uppercase
    if (data.dueDate) parts.push(`DT:${data.dueDate}`);

    return parts.join('*');
}

/**
 * Generate QR code image as data URL
 */
export async function generateQRCodeDataURL(spaydString: string): Promise<string> {
    try {
        const dataUrl = await QRCode.toDataURL(spaydString, {
            errorCorrectionLevel: 'M',
            type: 'image/png',
            width: 300,
            margin: 2
        });
        return dataUrl;
    } catch (error) {
        console.error('Error generating QR code:', error);
        throw error;
    }
}

/**
 * Test function to generate QR code with test data
 * Returns SPAYD string and data URL for testing
 */
export async function testQRCodeGeneration(): Promise<{ spayd: string; dataUrl: string }> {
    const testData = {
        account: 'CZ6508000000192000145399', // Test IBAN (Československá obchodní banka)
        amount: 1250.50,
        currency: 'CZK',
        variableSymbol: '2025001',
        message: 'Test payment for invoice'
    };

    const spayd = generateSPAYDString(testData);

    const dataUrl = await generateQRCodeDataURL(spayd);

    return { spayd, dataUrl };
}
